// Card decks and hands at the table (issue 1177), beside the log: every deck in play as THIS person was sent it — their
// own hand whole, everyone else's as a count, the draw pile as a count, the discard's top and count, the cards face up on
// the table. Read with the log's first page and moved only by the frames this person was sent; nothing here works out a
// card that was not sent. The DM's own decks (kept with the account) and placing one at the table go through
// `requestJson` (so `authFetch`) to this origin only; the shuffle is the server's.
import { parseDeckSpec, type DeckSpec, type ProjectedEvent, type SeenDeck, type UserDeck } from '@dragons-table/vtt-shared';
import { requestJson } from './api';

export type Decks = Record<string, SeenDeck>;
export type Answer<T> = { kind: 'ok'; value: T } | { kind: 'refused'; reason: string } | { kind: 'error' };

const enc = encodeURIComponent;
const DOWN = "The decks can't be read just now. Try again in a moment.";
const reasonOf = (status: number, body: unknown, fallback: string): Answer<never> => {
  const r = (body as { reason?: unknown } | null)?.reason;
  if (status === 0) return { kind: 'error' };
  return { kind: 'refused', reason: typeof r === 'string' ? r : fallback };
};

const isCard = (c: unknown): boolean => typeof (c as { id?: unknown } | null)?.id === 'string' && typeof (c as { label?: unknown }).label === 'string';

/** The decks a log page carries, as this person was sent them; anything malformed is left out. */
export function parsePageDecks(v: unknown): Decks {
  if (!Array.isArray(v)) return {};
  const out: Decks = {};
  for (const d of v as SeenDeck[]) {
    if (!d || typeof d.id !== 'string' || typeof d.name !== 'string' || typeof d.draw !== 'number' || !Array.isArray(d.mine) || !Array.isArray(d.table) || !d.mine.every(isCard) || !d.table.every(isCard)) continue;
    out[d.id] = d;
  }
  return out;
}

/** The decks moved by one frame: a deck as now seen, or its removal. A revealed card moves nothing (it is a notice). */
export function foldDecks(cur: Decks, ev: ProjectedEvent): Decks {
  if (ev.kind === 'deck') return { ...cur, [ev.deck.id]: ev.deck };
  if (ev.kind === 'deck.gone' && cur[ev.id]) { const { [ev.id]: _gone, ...rest } = cur; return rest; }
  return cur;
}

/** "2 cards", "1 card", "no cards". */
export const cardCount = (n: number): string => (n === 0 ? 'no cards' : n === 1 ? '1 card' : `${n} cards`);

/** The DM's own decks, each read through the shared parser, and the standard one offered as a starter. */
export async function myDecks(fetchImpl: typeof fetch = fetch): Promise<Answer<{ decks: UserDeck[]; standard: DeckSpec | null }>> {
  const { status, body } = await requestJson('/api/me/decks', {}, fetchImpl);
  const list = (body as { decks?: unknown } | null)?.decks;
  if (status !== 200 || !Array.isArray(list)) return reasonOf(status, body, DOWN);
  const decks = list.flatMap((d) => {
    const id = (d as { id?: unknown } | null)?.id;
    const parsed = parseDeckSpec({ name: (d as { name?: unknown }).name, cards: (d as { cards?: unknown }).cards });
    return typeof id === 'string' && parsed ? [{ id, ...parsed }] : [];
  });
  return { kind: 'ok', value: { decks, standard: parseDeckSpec((body as { standard?: unknown }).standard) } };
}

/** Save a new deck (no id) or replace one of the DM's own. */
export async function saveDeck(deck: DeckSpec, id: string | null, fetchImpl: typeof fetch = fetch): Promise<Answer<UserDeck>> {
  const { status, body } = await requestJson(id ? `/api/me/decks/${enc(id)}` : '/api/me/decks', { method: id ? 'PUT' : 'POST', body: JSON.stringify(deck) }, fetchImpl);
  const got = body as UserDeck | null;
  if ((status === 200 || status === 201) && got && typeof got.id === 'string') return { kind: 'ok', value: got };
  if (status === 400) return { kind: 'refused', reason: 'A deck needs a name and from 1 to 200 cards, each with a label.' };
  return reasonOf(status, body, DOWN);
}

export async function deleteDeck(id: string, fetchImpl: typeof fetch = fetch): Promise<Answer<null>> {
  const { status, body } = await requestJson(`/api/me/decks/${enc(id)}`, { method: 'DELETE' }, fetchImpl);
  return status === 204 ? { kind: 'ok', value: null } : reasonOf(status, body, DOWN);
}

/** Put a deck on the table: one of the DM's own, or the standard deck. The server shuffles it. */
export async function placeDeck(campaignId: string, from: { mine: string } | { standard: { jokers: boolean } }, fetchImpl: typeof fetch = fetch): Promise<Answer<{ id: string; name: string }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/decks`, { method: 'POST', body: JSON.stringify({ from }) }, fetchImpl);
  const got = body as { id?: unknown; name?: unknown } | null;
  if (status === 201 && typeof got?.id === 'string' && typeof got.name === 'string') return { kind: 'ok', value: { id: got.id, name: got.name } };
  return reasonOf(status, body, "That deck couldn't be put on the table just now.");
}
